// Pro's Library > Prompt templates (spec ui-perf/pro-library-tabs): a template's "Use in Image"
// takes its prompt and pictures to Pro's Image page in the navigation's state, as AI Actors'
// "Create a Talking Head" hands its actor over (`platform/actors/talking-head-handoff.ts`). The
// composer applies it as its own panel's Apply does, once per navigation.
import { useNavigate, type HistoryState, type ParsedLocation } from "@tanstack/react-router";

import type { PickedImage } from "../../media/ImagePicker";

/** What a template's Apply hands the composer: its prompt, its pictures and which it is. */
export interface HandedTemplate {
  prompt: string;
  images: PickedImage[];
  templateId: string;
}

declare module "@tanstack/react-router" {
  interface HistoryState {
    /** The prompt template "Use in Image" brings to Pro's Image page. */
    promptTemplate?: HandedTemplate;
  }
}

/** Opens Pro's Image page with the template applied. */
export function useTemplateInImage(teamId: string, brandId: string) {
  const navigate = useNavigate();
  return (prompt: string, images: PickedImage[], templateId: string) => {
    const handed: HandedTemplate = { prompt, images, templateId };
    void navigate({
      to: "/teams/$teamId/brands/$brandId/pro/create-image",
      params: { teamId, brandId },
      state: (previous: HistoryState) => ({ ...previous, promptTemplate: handed }),
    });
  };
}

/** The navigations whose template a composer already applied. */
const taken = new Set<string>();

const keyOf = (location: ParsedLocation) => location.state.__TSR_key ?? location.state.key;

/** The template this navigation brought, unless a composer already applied it. */
export function arrivingPromptTemplate(location: ParsedLocation): HandedTemplate | null {
  const handed = location.state.promptTemplate;
  const key = keyOf(location);
  if (handed === undefined || key === undefined || taken.has(key)) return null;
  return handed;
}

/** The composer applied the navigation's template: opened again on it, it starts without. */
export function tookPromptTemplate(location: ParsedLocation): void {
  const key = keyOf(location);
  if (key !== undefined) taken.add(key);
}
